import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import StorefrontLayout from "../../components/storefront/StorefrontLayout";
import ProductCard from "../../components/storefront/ProductCard";
import { api } from "../../lib/api";
import { Gem, Search, X } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { toggleWishlist, getGuestWishlist } from "../../lib/Wishlist";
import { toast } from "sonner";

// The three gemstone sub-types (stored on the product as gem_type / category).
const TYPES = ["Diamond", "Precious Stone", "Semi-precious Stone"];

const subType = (p) => p.gem_type || p.category || "";

export default function Gemstones() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState(new Set());

  const [q, setQ] = useState("");
  const [type, setType] = useState(params.get("type") || "");     // "" = All
  const [colour, setColour] = useState(params.get("colour") || "");

  useEffect(() => {
    setLoading(true);
    api.get("/api/products", { params: { metal: "Gemstone", limit: 200 } })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    if (user) {
      api.get("/api/wishlist").then((r) => setWishlist(new Set((r.data.items || []).map((p) => p.id)))).catch(() => {});
    } else {
      setWishlist(new Set(getGuestWishlist()));
    }
  }, [user]);

  // Keep the URL in step with the chosen type so the header dropdown links work.
  useEffect(() => {
    const next = {};
    if (type) next.type = type;
    if (colour) next.colour = colour;
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, colour]);

  const colours = useMemo(
    () => [...new Set(items.map((p) => (p.colour || "").trim()).filter(Boolean))].sort(),
    [items]
  );

  const filtered = useMemo(() => items.filter((p) => {
    if (type && subType(p).toLowerCase() !== type.toLowerCase()) return false;
    if (colour && (p.colour || "").toLowerCase() !== colour.toLowerCase()) return false;
    if (q && !(`${p.name} ${p.colour || ""} ${subType(p)}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  }), [items, type, colour, q]);

  const priceOf = (p) => (typeof p.calculated_price === "number" ? p.calculated_price : (typeof p.price === "number" ? p.price : null));

  const onWishlist = async (id) => {
    try {
      const { action } = await toggleWishlist(id, !!user);
      setWishlist((s) => { const n = new Set(s); action === "added" ? n.add(id) : n.delete(id); return n; });
      toast.success(`Wishlist ${action}`);
    } catch { toast.error("Could not update wishlist"); }
  };

  const clear = () => { setQ(""); setType(""); setColour(""); };
  const active = q || type || colour;

  return (
    <StorefrontLayout>
      {/* Hero */}
      <div className="bg-gradient-to-b from-maroon to-maroon-dark text-pearl">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-16 text-center">
          <div className="inline-flex items-center gap-2 eyebrow text-gold mb-3"><Gem size={16}/> Gemstones</div>
          <h1 className="font-display text-4xl lg:text-5xl">Diamonds &amp; Precious Stones</h1>
          <p className="text-pearl/70 mt-3 max-w-xl mx-auto text-sm">
            Certified diamonds, precious and semi-precious stones — priced by the carat.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mb-6">
          <div className="relative w-full sm:flex-1 sm:min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ash"/>
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or colour…"
              className="w-full border border-line pl-9 pr-3 py-2.5 bg-white text-sm rounded-lg"
              data-testid="gem-search"
            />
          </div>
          <div className="flex items-center gap-3">
            <select value={colour} onChange={(e) => setColour(e.target.value)} className="flex-1 sm:flex-none border border-line px-3 py-2.5 bg-white text-sm rounded-lg" data-testid="gem-colour">
              <option value="">All colours</option>
              {colours.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {active && (
              <button onClick={clear} className="inline-flex items-center gap-1 text-xs text-maroon underline shrink-0" data-testid="gem-clear"><X size={13}/> Clear</button>
            )}
          </div>
        </div>

        {/* Sub-type tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto sm:flex-wrap pb-1 -mx-4 px-4 sm:mx-0 sm:px-0">
          {["", ...TYPES].map((tp) => (
            <button
              key={tp || "all"}
              onClick={() => setType(tp)}
              className={`px-4 py-2 text-xs rounded-full border transition-colors whitespace-nowrap shrink-0 ${
                (type || "") === tp ? "bg-maroon text-pearl border-maroon" : "bg-white text-ink border-line hover:border-maroon"
              }`}
              data-testid={`gem-type-${tp ? tp.toLowerCase().replace(/\s+/g, "-") : "all"}`}
            >
              {tp || "All Gemstones"}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center text-ash py-20">Loading gemstones…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-ash py-20" data-testid="gem-empty">
            <Gem size={28} className="mx-auto mb-3 opacity-40"/>
            {items.length === 0 ? "No gemstones have been added yet." : "No gemstones match these filters."}
          </div>
        ) : (
          <>
            <div className="text-xs text-ash mb-3">{filtered.length} gemstone{filtered.length === 1 ? "" : "s"}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
              {filtered.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  price={priceOf(p)}
                  onWishlist={onWishlist}
                  isWishlisted={wishlist.has(p.id)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </StorefrontLayout>
  );
}
